import { QRCodeSVG } from 'qrcode.react';
import type { TemplateData } from './template-types';
import { TIPO_LABEL, SECCION_LABEL } from './template-types';

const HEADER_BG = '#1e3a5f';
const ACCENT    = '#3b82f6';

function QRBox({ url }: { url?: string }) {
  if (url) {
    return (
      <div style={{ width: 76, height: 76, border: '1.5px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
        <QRCodeSVG value={url} size={68} />
      </div>
    );
  }
  return (
    <div style={{ width: 76, height: 76, border: '1.5px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
      <svg viewBox="0 0 21 21" width="68" height="68">
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => { const p = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]]; return p[r]?.[c] ? <rect key={`a-${r}-${c}`} x={c*3} y={r*3} width={3} height={3} fill="#222" /> : null; }))}
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => { const p = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]]; return p[r]?.[c] ? <rect key={`b-${r}-${c}`} x={(14+c)*3} y={r*3} width={3} height={3} fill="#222" /> : null; }))}
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => { const p = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]]; return p[r]?.[c] ? <rect key={`c-${r}-${c}`} x={c*3} y={(14+r)*3} width={3} height={3} fill="#222" /> : null; }))}
      </svg>
    </div>
  );
}

function Contenido({ data }: { data: TemplateData }) {
  if (data.tipo === 'receta') return (
    <>
      {data.medicaciones?.map((m, i) => (
        <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span style={{ display: 'inline-block', background: ACCENT, color: '#fff', borderRadius: 4, width: 20, height: 20, lineHeight: '20px', textAlign: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
            <span style={{ fontWeight: 700, fontSize: 12 }}>{m.denominacionGenerica}{m.denominacionDistintiva ? <span style={{ fontWeight: 400, color: '#777', fontSize: 11 }}> ({m.denominacionDistintiva})</span> : ''}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginLeft: 28, marginTop: 4, fontSize: 10.5, color: '#444' }}>
            {m.dosis             && <span><span style={{ color: '#888' }}>Dosis:</span> {m.dosis}</span>}
            {m.frecuencia        && <span><span style={{ color: '#888' }}>Frecuencia:</span> {m.frecuencia}</span>}
            {m.duracion          && <span><span style={{ color: '#888' }}>Duración:</span> {m.duracion}</span>}
            {m.viaAdministracion && <span><span style={{ color: '#888' }}>Vía:</span> {m.viaAdministracion}</span>}
          </div>
          {m.indicaciones && <div style={{ marginLeft: 28, marginTop: 2, fontSize: 10, color: '#777', fontStyle: 'italic' }}>{m.indicaciones}</div>}
        </div>
      ))}
      {(!data.medicaciones?.length) && <p style={{ color: '#aaa', fontStyle: 'italic', fontSize: 11 }}>No se incluyeron medicamentos.</p>}
    </>
  );
  if (data.tipo === 'orden') return (
    <div style={{ fontSize: 12, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
      {data.instrucciones || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Sin indicaciones.</span>}
    </div>
  );
  if (data.tipo === 'presupuesto') {
    const items    = data.presupuestoItems ?? [];
    const moneda   = data.presupuestoMoneda ?? 'MXN';
    const subtotal = items.reduce((s, i) => s + i.cantidad * i.precioUnitario, 0);
    const descPct  = data.presupuestoDescuento ?? 0;
    const descAmt  = subtotal * (descPct / 100);
    const ivaPct   = data.presupuestoIva ?? 0;
    const ivaAmt   = (subtotal - descAmt) * (ivaPct / 100);
    const total    = subtotal - descAmt + ivaAmt;
    const fmt      = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 12px', padding: '6px 8px', background: HEADER_BG, color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: '4px 4px 0 0' }}>
          <span>Servicio</span>
          <span style={{ textAlign: 'right', minWidth: 28 }}>Cant.</span>
          <span style={{ textAlign: 'right', minWidth: 72 }}>Precio u.</span>
          <span style={{ textAlign: 'right', minWidth: 80 }}>Total</span>
        </div>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 12px', padding: '8px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 11, alignItems: 'center', background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{item.servicio}</div>
              {item.descripcion && <div style={{ fontSize: 9.5, color: '#888' }}>{item.descripcion}</div>}
            </div>
            <span style={{ textAlign: 'right', color: '#777', minWidth: 28 }}>{item.cantidad}</span>
            <span style={{ textAlign: 'right', minWidth: 72 }}>{fmt(item.precioUnitario)}</span>
            <span style={{ textAlign: 'right', fontWeight: 600, minWidth: 80 }}>{fmt(item.cantidad * item.precioUnitario)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, marginTop: 12, fontSize: 11 }}>
          <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#888' }}>Subtotal</span><span style={{ minWidth: 80, textAlign: 'right' }}>{fmt(subtotal)}</span></div>
          {descPct > 0 && <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#888' }}>Descuento ({descPct}%)</span><span style={{ minWidth: 80, textAlign: 'right', color: '#16a34a' }}>−{fmt(descAmt)}</span></div>}
          {ivaPct > 0  && <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#888' }}>IVA ({ivaPct}%)</span><span style={{ minWidth: 80, textAlign: 'right' }}>{fmt(ivaAmt)}</span></div>}
          <div style={{ display: 'flex', gap: 16, background: HEADER_BG, color: '#fff', padding: '6px 10px', borderRadius: 4, marginTop: 2 }}>
            <span style={{ fontWeight: 700 }}>TOTAL {moneda}</span>
            <span style={{ minWidth: 80, textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(total)}</span>
          </div>
          {data.presupuestoValidezDias && <div style={{ fontSize: 9.5, color: '#888', marginTop: 4 }}>Vigencia: {data.presupuestoValidezDias} días</div>}
        </div>
      </div>
    );
  }
  if (data.tipo === 'plan-tratamiento') {
    const items = data.planTratamientoItems ?? [];
    return (
      <div>
        {(data.diagnostico || data.objetivoTratamiento) && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 11 }}>
            {data.diagnostico        && <div><span style={{ color: '#888', fontSize: 10 }}>Diagnóstico:</span> <b>{data.diagnostico}</b></div>}
            {data.objetivoTratamiento && <div style={{ marginTop: 4 }}><span style={{ color: '#888', fontSize: 10 }}>Objetivo:</span> <b>{data.objetivoTratamiento}</b></div>}
          </div>
        )}
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ display: 'inline-block', background: HEADER_BG, color: '#fff', borderRadius: 4, width: 20, height: 20, lineHeight: '20px', textAlign: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
            <div>
              <div style={{ fontWeight: 600 }}>{item.tratamiento}</div>
              {item.descripcion && <div style={{ fontSize: 10.5, color: '#777', marginTop: 2 }}>{item.descripcion}</div>}
              <div style={{ display: 'flex', gap: 16, marginTop: 3, fontSize: 10, color: '#888' }}>
                {item.sesiones !== undefined && <span>Sesiones: <b>{item.sesiones}</b></span>}
                {item.duracion               && <span>Duración: <b>{item.duracion}</b></span>}
              </div>
            </div>
          </div>
        ))}
        {(data.duracionTratamiento || data.frecuenciaTratamiento) && (
          <div style={{ marginTop: 12, padding: '7px 12px', background: `${ACCENT}15`, border: `1px solid ${ACCENT}30`, borderRadius: 6, fontSize: 10.5, color: HEADER_BG }}>
            {data.duracionTratamiento  && <span><b>Duración total:</b> {data.duracionTratamiento}</span>}
            {data.duracionTratamiento && data.frecuenciaTratamiento && <span style={{ margin: '0 10px', opacity: 0.3 }}>│</span>}
            {data.frecuenciaTratamiento && <span><b>Frecuencia:</b> {data.frecuenciaTratamiento}</span>}
          </div>
        )}
      </div>
    );
  }
  if (data.tipo === 'consentimiento') return (
    <div>
      {data.consentimientoTitulo && <div style={{ fontWeight: 700, fontSize: 12.5, color: HEADER_BG, marginBottom: 10 }}>{data.consentimientoTitulo}</div>}
      <div style={{ fontSize: 11.5, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
        {data.consentimientoContenido || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Sin contenido.</span>}
      </div>
    </div>
  );
  return (
    <>
      {data.estudios?.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
          <span style={{ display: 'inline-block', background: HEADER_BG, color: '#fff', borderRadius: 4, width: 20, height: 20, lineHeight: '20px', textAlign: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
          <div>
            <div style={{ fontWeight: 600 }}>{e.nombre}</div>
            {e.descripcion && <div style={{ fontSize: 10.5, color: '#777' }}>{e.descripcion}</div>}
          </div>
        </div>
      ))}
    </>
  );
}

function FirmaPacienteBox({ data }: { data: TemplateData }) {
  if (data.tipo !== 'consentimiento') return null;
  return (
    <div style={{ textAlign: 'center' }}>
      {data.firmaUrl
        ? (data.firmaEsPdf
          ? <div style={{ fontSize: 9.5, color: '#777', fontStyle: 'italic', width: 200 }}>Documento firmado adjunto (PDF)</div>
          : <img src={data.firmaUrl} alt="Firma del paciente" style={{ height: 50, maxWidth: 200, objectFit: 'contain' }} />)
        : <div style={{ height: 50, width: 200 }} />}
      <div style={{ borderTop: '1.5px solid #333', width: 200, marginTop: 6, marginBottom: 6 }} />
      <div style={{ fontSize: 10.5, fontWeight: 600 }}>{data.paciente.nombre}</div>
      <div style={{ fontSize: 9.5, color: '#777' }}>Firma del paciente</div>
    </div>
  );
}

export function TemplateCorporativo({ data, width = 794, height = 1123 }: { data: TemplateData; width?: number; height?: number }) {
  const tituloDoc = TIPO_LABEL[data.tipo];
  return (
    <div style={{ width, height, fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 11, color: '#1a1a1a', backgroundColor: '#fff', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>

      {/* Header band */}
      <div style={{ background: HEADER_BG, color: '#fff', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 52, height: 52, background: 'rgba(255,255,255,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1.5px solid rgba(255,255,255,0.3)' }}>
            {data.clinica.logoUrl
              ? <img src={data.clinica.logoUrl} alt="" style={{ width: 46, height: 46, objectFit: 'contain', borderRadius: 6 }} />
              : <span style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>{data.clinica.nombre.charAt(0)}</span>}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: 0.3 }}>{data.clinica.nombre}</div>
            {data.clinica.cedula && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{data.clinica.cedula}</div>}
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>{data.medico.nombre}</div>
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, fontSize: 13, letterSpacing: 0.3 }}>{tituloDoc.toUpperCase()}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>Folio: <b style={{ color: '#fff' }}>{data.folio}</b></div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{data.fecha}</div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '24px 40px', display: 'flex', flexDirection: 'column' }}>

        {/* Patient row */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 32, rowGap: 8, marginBottom: 20 }}>
          <div><span style={{ color: '#888', fontSize: 10 }}>Paciente</span><br /><b style={{ fontSize: 12 }}>{data.paciente.nombre}</b></div>
          <div><span style={{ color: '#888', fontSize: 10 }}>Fecha de nacimiento</span><br /><b>{data.paciente.fechaNacimiento ?? '—'}</b></div>
          <div><span style={{ color: '#888', fontSize: 10 }}>Sexo</span><br /><b>{data.paciente.sexo ?? '—'}</b></div>
          <div><span style={{ color: '#888', fontSize: 10 }}>Fecha de consulta</span><br /><b>{data.fechaConsulta ?? '—'}</b></div>
        </div>

        {/* Section pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ background: HEADER_BG, color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
            {SECCION_LABEL[data.tipo]}
          </div>
          <div style={{ flex: 1, borderTop: '1px solid #e2e8f0' }} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Contenido data={data} />
        </div>

        {/* QR + Signature(s) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 40 }}>
          <QRBox url={data.qrUrl} />
          <FirmaPacienteBox data={data} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ borderTop: '1.5px solid #333', width: 200, marginBottom: 6 }} />
            <div style={{ fontSize: 10.5, fontWeight: 600 }}>{data.medico.nombre}</div>
            {data.medico.cedula && <div style={{ fontSize: 9.5, color: '#777' }}>Cédula: {data.medico.cedula}</div>}
          </div>
        </div>
      </div>

      {/* Footer band */}
      <div style={{ background: HEADER_BG, color: 'rgba(255,255,255,0.75)', padding: '10px 40px', display: 'flex', justifyContent: 'space-between', fontSize: 9.5, marginTop: 'auto' }}>
        <span>{data.clinica.direccion}</span>
        {data.clinica.telefono && <span>Tel. {data.clinica.telefono}</span>}
        <span>Receta electrónica</span>
      </div>
    </div>
  );
}
